import React, { useId } from "react";
import { cn } from "../../lib/cn";

/* -------------------------------------------------------------------------- */
/* Shared control styling                                                     */
/* -------------------------------------------------------------------------- */

const CONTROL = cn(
  "border-line bg-surface-sunken text-ink placeholder:text-ink-subtle",
  "w-full rounded-lg border px-3 text-sm",
  "transition-colors duration-(--duration-instant) ease-(--ease-standard)",
  "focus:border-accent focus:bg-surface focus:outline-none",
  "disabled:cursor-not-allowed disabled:opacity-55",
);

/* -------------------------------------------------------------------------- */
/* Field wrapper                                                              */
/* -------------------------------------------------------------------------- */

export type FieldProps = {
  label: React.ReactNode;
  /** Help text below the control. */
  hint?: React.ReactNode;
  error?: string | null;
  children: (props: { id: string; "aria-describedby"?: string }) => React.ReactNode;
  className?: string;
};

/**
 * Pairs a label with a control and wires up id / aria-describedby, so every
 * form field on the admin screens is labelled without repeating the plumbing.
 */
export function Field({ label, hint, error, children, className }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(" ") || undefined;

  return (
    <div className={cn("min-w-0", className)}>
      <label
        htmlFor={id}
        className="text-ink-muted mb-1.5 block text-xs font-semibold tracking-wide uppercase"
      >
        {label}
      </label>

      {children({ id, "aria-describedby": describedBy })}

      {error && (
        <p id={errorId} className="text-danger animate-fade-in mt-1.5 text-xs font-medium">
          {error}
        </p>
      )}
      {hint && !error && (
        <p id={hintId} className="text-ink-subtle mt-1.5 text-xs leading-relaxed">
          {hint}
        </p>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Controls                                                                   */
/* -------------------------------------------------------------------------- */

export const Input = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(function Input({ className, ...rest }, ref) {
  return <input ref={ref} className={cn(CONTROL, "h-10", className)} {...rest} />;
});

export const Select = React.forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement>
>(function Select({ className, multiple, ...rest }, ref) {
  return (
    <select
      ref={ref}
      multiple={multiple}
      className={cn(CONTROL, multiple ? "py-2" : "h-10", className)}
      {...rest}
    />
  );
});

export const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cn(CONTROL, "py-2 leading-relaxed", className)} {...rest} />;
});

/* -------------------------------------------------------------------------- */
/* Switch                                                                     */
/* -------------------------------------------------------------------------- */

export type SwitchProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: React.ReactNode;
  hint?: React.ReactNode;
  disabled?: boolean;
  name?: string;
};

/**
 * A real checkbox under the visual track, so it stays keyboard- and
 * screen-reader-native rather than being a styled div with click handlers.
 */
export function Switch({
  checked,
  onChange,
  label,
  hint,
  disabled = false,
  name,
}: SwitchProps) {
  return (
    <label
      className={cn(
        "group flex items-start gap-3",
        disabled ? "cursor-not-allowed opacity-55" : "cursor-pointer",
      )}
    >
      <span className="relative mt-0.5 inline-flex shrink-0">
        <input
          type="checkbox"
          name={name}
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
          className="peer sr-only"
        />
        <span
          aria-hidden="true"
          className={cn(
            "bg-line-strong peer-checked:bg-accent block h-5 w-9 rounded-full",
            "transition-colors duration-(--duration-fast) ease-(--ease-standard)",
            "peer-focus-visible:outline peer-focus-visible:outline-2",
            "peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent",
          )}
        />
        <span
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute top-0.5 left-0.5 block h-4 w-4 rounded-full bg-white shadow-sm",
            "transition-transform duration-(--duration-fast) ease-(--ease-standard)",
            "peer-checked:translate-x-4",
          )}
        />
      </span>

      <span className="min-w-0 leading-tight">
        <span className="text-ink block text-[13px] font-medium">{label}</span>
        {hint && (
          <span className="text-ink-subtle mt-1 block text-xs leading-relaxed">
            {hint}
          </span>
        )}
      </span>
    </label>
  );
}
